import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import MultiCarousel from 'react-multi-carousel';
import CustomSliderArrow from 'components/ui/CustomSliderArrow';
import Icon from 'components/ui/Icon';
import Image from 'components/ui/Image';
import tailwind from 'utils/breakpoints';
import styles from './Carousel.module.scss';
import type { IBlockCarouselItem } from 'types/contentful/generated';

type SlidesProps = {
  slides: Slide[];
  expand?: boolean;
  update?: (index: number) => void;
  setSlideCount?: (count: number) => void;
  enableControls: boolean;
  overlayClick?: boolean;
};

const Slides = ({
  slides,
  expand,
  update,
  setSlideCount,
  enableControls,
  overlayClick,
}: SlidesProps): JSX.Element => {
  const sliderRef = useRef<MultiCarousel | null>(null);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);

  useEffect(() => {
    if (setSlideCount) {
      setSlideCount(slides.length);
    }
  }, []);

  const currentSlide = slides[currentSlideIndex];

  return (
    <div className="relative">
      <MultiCarousel
        ref={sliderRef}
        responsive={{
          all: {
            breakpoint: { max: 4000, min: 0 },
            items: 1,
          },
        }}
        arrows={enableControls}
        customLeftArrow={
          <CustomSliderArrow
            direction="left"
            className={styles.carouselArrowLeft}
          />
        }
        customRightArrow={
          <CustomSliderArrow
            direction="right"
            className={styles.carouselArrowRight}
          />
        }
        containerClass={`flex relative items-center overflow-hidden`}
        sliderClass="flex relative flex-row list-none p-0 m-0"
        itemClass="shrink-0"
        infinite
        beforeChange={(nextSlide, { currentSlide }) => {
          let nextIndex = currentSlideIndex;
          if (nextSlide > currentSlide) {
            nextIndex++;
          } else {
            nextIndex--;
          }

          if (nextIndex === slides.length) {
            update?.(0);
            setCurrentSlideIndex(0);
            return;
          }

          if (nextIndex === -1) {
            update?.(slides.length - 1);
            setCurrentSlideIndex(slides.length - 1);
            return;
          }

          update?.(nextIndex);
          setCurrentSlideIndex(nextIndex);
        }}
      >
        {slides.map((slide, i) => (
          <div
            key={i}
            className={clsx(
              'relative flex justify-center shrink-0',
              styles.slide,
            )}
          >
            <Image
              className="rounded-md object-cover"
              src={slide.image.url || ''}
              alt={slide.image.description || slide.description || ''}
              contentful
              fill
            />
            {overlayClick && (
              <>
                <div
                  className="absolute top-0 left-0 h-full w-2/4 cursor-pointer"
                  onClick={() => {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    sliderRef.current?.previous();
                  }}
                />
                <div
                  className="absolute top-0 right-0 h-full w-2/4 cursor-pointer"
                  onClick={() => {
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    sliderRef.current?.next();
                  }}
                />
              </>
            )}
          </div>
        ))}
      </MultiCarousel>
      {!expand && (
        <div className="relative">
          <div
            className={clsx(
              'flex absolute w-full pb-2 justify-center lg:hidden',
              styles.dots,
            )}
          >
            {[...Array(slides.length).keys()].map((_, i) => (
              <div
                key={i}
                className={clsx(
                  'w-3 h-3 bg-white rounded-full mx-1',
                  currentSlideIndex !== i && 'opacity-50',
                )}
              />
            ))}
          </div>
          <div className="pt-3 px-6 flex justify-between">
            {currentSlide?.description && (
              <p className="text-primary">{currentSlide.description}</p>
            )}
            <div className="font-bold text-secondary">
              {currentSlideIndex + 1}/{slides.length}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

type Slide = {
  description?: string;
  image: { url?: string; description?: string };
};

type Props = {
  blockId?: string;
  title?: string;
  slides: { items?: Slide[] } | IBlockCarouselItem[];
};

const Carousel = ({
  blockId,
  title,
  slides: givenSlides,
}: Props): JSX.Element | null => {
  const [expand, setExpand] = useState(false);
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [slideCount, setSlideCount] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const nodeRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setIsMobile(window.matchMedia('(max-width: 1023px)').matches);
  }, []);

  let slides: Slide[];
  const isGql = 'items' in givenSlides;
  if (isGql) {
    slides = givenSlides.items || [];
  } else {
    slides = (givenSlides as IBlockCarouselItem[])
      .filter((entry) => !!entry.fields)
      .map((entry) => {
        const { description, image } = entry.fields;
        const imageFields = image.fields;

        const slide: Slide = {
          description,
          image: {
            url: imageFields.file.url,
            description: imageFields.description,
          },
        };

        return slide;
      });
  }

  if (!slides?.length) {
    return null;
  }

  const currentSlide = slides[currentSlideIndex];

  return (
    <div id={blockId} className="my-8 max-w-screen-xl mx-auto px-5 md:px-4">
      {title && <h2 className="text-3xl">{title}</h2>}
      <div
        ref={nodeRef}
        onClick={() => {
          if (!isMobile) {
            setExpand(true);
          }
        }}
      >
        <Slides
          slides={slides}
          enableControls={!isMobile && slides.length > 1}
        />
      </div>
      <div
        className={clsx(
          'bg-white fixed top-0 left-0 w-full h-full z-50 flex justify-center items-center',
          expand
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none',
        )}
      >
        <div
          className="absolute top-0 right-0 p-12"
          onClick={() => {
            setExpand(false);
          }}
        >
          <Icon
            type="Xmark"
            color={tailwind.theme.backgroundColor.tertiary}
            className="w-8"
          />
        </div>
        <div className="grid grid-cols-3 gap-8 w-4/5">
          <div className="col-span-2">
            <Slides
              slides={slides}
              update={(index) => {
                setCurrentSlideIndex(index);
              }}
              setSlideCount={(count) => {
                setSlideCount(count);
              }}
              expand
              overlayClick={!isMobile}
              enableControls={slides.length > 1}
            />
          </div>
          <div className="col-span-1">
            <div className="font-bold text-secondary mb-8">
              {currentSlideIndex + 1}/{slideCount}
            </div>
            {currentSlide?.description && (
              <p className="text-primary">{currentSlide.description}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Carousel;
